// Splits an expense (or income) into several documents: every row becomes a copy of the document with its own
// account, amount, VAT, months and currency, and the original document keeps the amount that is left.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Portal, IconButton, Tooltip, TextField } from '@mui/material';
import Iframe from 'react-iframe';
import CloseIcon from '@mui/icons-material/Cancel';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Draggable from 'react-draggable';
import AutocompleteSelectFilter from '../Widgets/AutocompleteSelectFilter/AutocompleteSelectFilter.jsx';
import CustomMonthDatePicker from '../Widgets/CustomDates/CustomMonthDatePicker/CustomMonthDatePicker';
import DocumentService from '../../Services/documents.service';
import ForeignCurrencyService from '../../Services/foreignCurrency.service';
import Constants from '../../assests/Constants/constants';
import { calculateVatAmount, calculateGrossFromNet } from '../../Utils/GetVatValue';
import { beautifyNumber, parseBeautifiedNumber } from '../../Utils/FormatNumber';
import { evaluateAmountExpression, isPlainNumber } from '../../Utils/evaluateAmountExpression';
import fieldStyles from '../JournalEntryies/SubComponents/AddJournalEntryModal.module.scss';
import modalStyles from '../JournalEntryies/SubComponents/JournalEntryTemplatesModal.module.scss';
import shellStyles from '../PrimaryBulkActionsToolbar/BulkEditModal.module.scss';
import styles from './SplitDocumentModal.module.scss';

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
const hasValue = (v) => v !== '' && v !== null && v !== undefined;
const DEFAULT_VAT = 100;
const ADD_ACCOUNT_CARD = '__addAccountCard__';

let rowSeq = 0;

// Amount input that also takes arithmetic ("100+100"); Enter or leaving the field replaces it with the result.
function AmountField({ value, onChange, disabled, placeholder, inputStyle }) {
    const commit = () => {
        if (isPlainNumber(value) || !hasValue(value)) return;
        const result = evaluateAmountExpression(value);
        if (result !== null) onChange(String(round2(result)));
    };
    return (
        <TextField
            value={value}
            placeholder={placeholder}
            disabled={disabled}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={(e) => {
                if (e.key === 'Enter') {
                    e.preventDefault();
                    commit();
                }
            }}
            onBlur={commit}
            variant="standard"
            InputProps={{ disableUnderline: true }}
            className={fieldStyles.styleInput}
            size="small"
            inputProps={{ ...inputStyle, inputMode: 'decimal' }}
        />
    );
}

export default function SplitDocumentModal({
    open,
    onClose,
    onSplit,            // async ({ remaining, foreignCurrencyAmount }) => void — after the split was saved
    document: source,   // the expense / income being split
    docType,            // "Expenses" | "Incomes"
    formattedAccountCardOptions = [],
    sortCodeIdOptions = [],
    onRequestAddAccountCard,   // () => void — opens the parent's AddAccountCardModal
    applyAccountCardRef,       // ref the parent calls with the id of the card it just created
    client,
    token,
}) {
    const { t, i18n } = useTranslation();
    const dir = i18n.dir();
    const [container] = useState(() => window.document.createElement('div'));
    const nodeRef = useRef(null);
    const pendingAccountCardRowRef = useRef(null);
    const [busy, setBusy] = useState(false);
    const [rates, setRates] = useState({});
    // Same side pane as the edit modal: the document being split stays in view while the lines are filled in.
    const [showDocument, setShowDocument] = useState(true);

    const newRow = (from) => ({
        key: ++rowSeq,
        accountId: '',
        amount: '',
        netAmount: '',
        vat: from?.vat ?? DEFAULT_VAT,
        description: '',
        sortCodeId: from?.sortCodeId ?? source?.sortCodeId ?? '',
        reportingMonthDate: from?.reportingMonthDate ?? source?.reportingMonthDate ?? null,
        registrationMonthDate: from?.registrationMonthDate ?? source?.registrationMonthDate ?? null,
        foreignCurrencyCode: from ? from.foreignCurrencyCode : (source?.foreignCurrencyCode ?? ''),
        foreignCurrencyAmount: '',
    });
    const [rows, setRows] = useState(() => [newRow()]);

    const isExpense = docType === 'Expenses';
    // The split lines sit on the document's own side: debit for an expense, credit for an income.
    const accountCardLabel = isExpense ? t('DebitAccountCard') : t('CreditAccountCard');
    const sourceAccount = isExpense ? source?.debitAccount : source?.creditAccount;
    const partyAccount = isExpense ? source?.creditAccount : source?.debitAccount;
    const documentDate = source?.documentDate;

    const canAddAccountCard = typeof onRequestAddAccountCard === 'function';
    const options = useMemo(
        () => {
            const mapped = (formattedAccountCardOptions || []).map((o) => ({
                ...o,
                value: o.value ?? o.cardId,
                label: `${o.cardId} - ${o.name}`,
            }));
            // Same affordance as the journal entry rows: a card missing from the list can be
            // opened right here instead of leaving the split half filled in.
            return canAddAccountCard
                ? [...mapped, { value: ADD_ACCOUNT_CARD, label: t('AddAccountCard'), kind: 'add' }]
                : mapped;
        },
        [formattedAccountCardOptions, canAddAccountCard, t]
    );
    const vatOptions = useMemo(
        () => Constants.VATPercentage.map((v) => ({ value: v, label: v === 16 ? t('AccountingTax') : `${v}%` })),
        [t]
    );
    const sortCodeOptions = useMemo(
        () =>
            (sortCodeIdOptions || []).map((o) => ({
                value: o.sortCodeId,
                label: `${o.sortCodeId} - ${o.sortCodeName}`,
            })),
        [sortCodeIdOptions]
    );
    const currencyOptions = useMemo(
        () => Object.entries(Constants.ForeignCurrencyCode).map(([code, num]) => ({
            value: num,
            label: `${code} ${Constants.ForeignCurrencySymbol[code] || ''}`.trim(),
        })),
        []
    );

    const accountLabel = (id) => {
        if (!hasValue(id)) return '—';
        return options.find((o) => String(o.value) === String(id))?.label || String(id);
    };
    const sortCodeLabel = (id) => {
        if (!hasValue(id)) return '—';
        return sortCodeOptions.find((o) => String(o.value) === String(id))?.label || String(id);
    };
    const findOption = (list, id) =>
        !hasValue(id) ? null : list.find((o) => String(o.value) === String(id)) || { value: id, label: String(id) };

    const amount = parseBeautifiedNumber(source?.amount) || 0;
    const amountBeforeVat = round2(amount - calculateVatAmount(amount, source?.vat, documentDate));

    const netFromGross = (gross, vat) => round2(gross - calculateVatAmount(gross, Number(vat), documentDate));
    const grossFromNet = (net, vat) => round2(calculateGrossFromNet(net, Number(vat), documentDate));
    const amountOf = (r) => evaluateAmountExpression(r.amount);
    const foreignAmountOf = (r) => evaluateAmountExpression(r.foreignCurrencyAmount);

    const total = round2(rows.reduce((sum, r) => sum + (amountOf(r) || 0), 0));
    const remaining = round2(amount - total);
    const exceeded = total > 0 && remaining <= 0;

    // A row without an account card produces a document without one, to be assigned later,
    // so only the amounts have to hold up.
    const rowsValid = rows.length > 0 && rows.every(
        (r) => amountOf(r) > 0
            && (!hasValue(r.foreignCurrencyCode) || foreignAmountOf(r) > 0)
    );

    useEffect(() => {
        if (!open) return undefined;
        window.document.body.appendChild(container);
        return () => { try { window.document.body.removeChild(container); } catch { /* ignore */ } };
    }, [open, container]);

    // Reopening starts with the document in view again, the way the edit modal does.
    useEffect(() => {
        if (open) setShowDocument(Boolean(source?.documentUrl));
    }, [open, source?.documentUrl]);

    // The parent owns the AddAccountCardModal, so it hands the new card back here and the row
    // that asked for it picks it up.
    useEffect(() => {
        if (!applyAccountCardRef) return undefined;
        applyAccountCardRef.current = (cardId) => {
            const rowKey = pendingAccountCardRowRef.current;
            pendingAccountCardRowRef.current = null;
            if (rowKey == null || !hasValue(cardId)) return;
            setRows((prev) => prev.map((r) => (r.key === rowKey ? { ...r, accountId: String(cardId) } : r)));
        };
        return () => { applyAccountCardRef.current = null; };
    }, [applyAccountCardRef]);

    // A document already in a foreign currency starts every row in it, so have its rate ready.
    useEffect(() => {
        const code = source?.foreignCurrencyCode;
        if (!open || !hasValue(code) || !documentDate || !token) return undefined;
        let cancelled = false;
        ForeignCurrencyService.GetIlsRateForDateWithFallback(code, documentDate, token)
            .then(({ rate }) => { if (!cancelled) setRates((prev) => ({ ...prev, [code]: rate })); })
            .catch(() => { /* the amounts stay editable by hand */ });
        return () => { cancelled = true; };
    }, [open, source?.foreignCurrencyCode, documentDate, token]);

    if (!open || !source) return null;

    const updateRow = (key, changesOf) =>
        setRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...changesOf(r) } : r)));

    const requestAddAccountCard = (rowKey) => {
        pendingAccountCardRowRef.current = rowKey;
        onRequestAddAccountCard?.();
    };
    const removeRow = (key) => setRows((prev) => (prev.length > 1 ? prev.filter((r) => r.key !== key) : prev));
    const rateOf = (r) => (hasValue(r.foreignCurrencyCode) ? rates[r.foreignCurrencyCode] : null);

    // The typed field keeps the raw text (it may still be "100+"); the other amounts follow once it is a number.
    const foreignFor = (r, gross, rate = rateOf(r)) =>
        rate ? { foreignCurrencyAmount: String(round2(gross / rate)) } : {};

    const handleGrossChange = (key, raw) => updateRow(key, (r) => {
        if (!isPlainNumber(raw)) return { amount: raw };
        const gross = Number(raw);
        return { amount: raw, netAmount: String(netFromGross(gross, r.vat)), ...foreignFor(r, gross) };
    });

    const handleNetChange = (key, raw) => updateRow(key, (r) => {
        if (!isPlainNumber(raw)) return { netAmount: raw };
        const gross = grossFromNet(Number(raw), r.vat);
        return { netAmount: raw, amount: String(gross), ...foreignFor(r, gross) };
    });

    const handleForeignAmountChange = (key, raw) => updateRow(key, (r) => {
        const rate = rateOf(r);
        if (!isPlainNumber(raw) || !rate) return { foreignCurrencyAmount: raw };
        const gross = round2(Number(raw) * rate);
        return { foreignCurrencyAmount: raw, amount: String(gross), netAmount: String(netFromGross(gross, r.vat)) };
    });

    const handleVatChange = (key, vat) => updateRow(key, (r) => {
        const gross = amountOf(r);
        return gross > 0 ? { vat, netAmount: String(netFromGross(gross, vat)) } : { vat };
    });

    const loadRate = async (code) => {
        if (!hasValue(code) || !documentDate) return null;
        if (rates[code]) return rates[code];
        try {
            const { rate } = await ForeignCurrencyService.GetIlsRateForDateWithFallback(code, documentDate, token);
            setRates((prev) => ({ ...prev, [code]: rate }));
            return rate;
        } catch {
            return null;
        }
    };

    const handleCurrencyChange = async (key, code) => {
        if (!hasValue(code)) {
            updateRow(key, () => ({ foreignCurrencyCode: '', foreignCurrencyAmount: '' }));
            return;
        }
        updateRow(key, () => ({ foreignCurrencyCode: code }));
        const rate = await loadRate(code);
        if (!rate) return;
        updateRow(key, (r) => {
            const fc = foreignAmountOf(r);
            if (fc > 0) {
                const gross = round2(fc * rate);
                return { amount: String(gross), netAmount: String(netFromGross(gross, r.vat)) };
            }
            const gross = amountOf(r);
            return gross > 0 ? foreignFor(r, gross, rate) : {};
        });
    };

    // Representative when the two amounts follow the day's rate, otherwise the rate they imply.
    const exchangeRateOf = (r) => {
        if (!hasValue(r.foreignCurrencyCode)) return {};
        const fc = foreignAmountOf(r);
        const nis = amountOf(r);
        const rate = rates[r.foreignCurrencyCode];
        const implied = fc > 0 ? nis / fc : null;
        if (rate && implied && Math.abs(implied - rate) <= rate * 0.001) {
            return { exchangeRateType: Constants.ExchangeRateType.Representative, exchangeRate: rate };
        }
        return implied
            ? { exchangeRateType: Constants.ExchangeRateType.Manual, exchangeRate: Number(implied.toFixed(6)) }
            : {};
    };

    const handleSave = async () => {
        if (!rowsValid || exceeded) return;
        setBusy(true);
        try {
            const lines = rows.map((r) => ({
                accountId: hasValue(r.accountId) ? String(r.accountId) : null,
                amount: round2(amountOf(r)),
                description: r.description.trim() || null,
                sortCodeId: hasValue(r.sortCodeId) ? String(r.sortCodeId) : null,
                vat: Number(r.vat),
                reportingMonthDate: r.reportingMonthDate || null,
                registrationMonthDate: r.registrationMonthDate || null,
                foreignCurrencyCode: hasValue(r.foreignCurrencyCode) ? Number(r.foreignCurrencyCode) : null,
                foreignCurrencyAmount: hasValue(r.foreignCurrencyCode) ? round2(foreignAmountOf(r)) : null,
                ...exchangeRateOf(r),
            }));
            const res = await DocumentService.SplitDocument(docType, source.id, lines, client.caseId, token);
            if (!res.ok) {
                const body = await res.json().catch(() => null);
                toast.error(body?.message || t('SplitExceedsAmount'));
                return;
            }
            const result = await res.json().catch(() => null);
            toast.success(t('documentUpdateSuccess'));
            if (onSplit) await onSplit(result);
            onClose();
        } catch (err) {
            toast.error(err?.message || t('documentUpdateError'));
        } finally {
            setBusy(false);
        }
    };

    const inputStyle = { style: { textAlign: dir === 'ltr' ? 'left' : 'right' } };
    const documentUrl = source.documentUrl || '';
    const previewOpen = showDocument && Boolean(documentUrl);

    return (
        <Portal container={container}>
            <div
                className={`${shellStyles.overlay} ${dir === 'rtl' ? shellStyles.rtl : shellStyles.ltr}`}
                onClick={busy ? undefined : onClose}
            >
                <Draggable
                    handle=".split-document-drag-handle"
                    cancel="button, .MuiIconButton-root, input, textarea, [class*='trigger']"
                    nodeRef={nodeRef}
                >
                    <div
                        ref={nodeRef}
                        className={`${shellStyles.dragShell} ${previewOpen ? styles.dragShellWithDocument : shellStyles.dragShellWide}`}
                        dir={dir}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className={`${shellStyles.modal} ${previewOpen ? styles.modalWithDocument : shellStyles.modalWide}`}>
                            <div className={styles.layout}>
                            <div className={styles.formPane}>
                            <div className={`${modalStyles.body} ${styles.formBody}`}>
                                <div className={`${modalStyles.header} split-document-drag-handle ${shellStyles.dragHandle}`}>
                                    <Tooltip title={t('Cancel')}>
                                        <IconButton onClick={onClose} className={modalStyles.closeIcon} disabled={busy}>
                                            <CloseIcon className={modalStyles.closeIcon} />
                                        </IconButton>
                                    </Tooltip>
                                    <div>{t('SplitDocument')}</div>
                                    <div className={styles.headerActions}>
                                        {documentUrl && (
                                            <Tooltip title={previewOpen ? t('HideDocument') : t('Document')}>
                                                <IconButton
                                                    onClick={() => setShowDocument((prev) => !prev)}
                                                    className={modalStyles.closeIcon}
                                                    disabled={busy}
                                                >
                                                    {previewOpen ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        <button
                                            type="button"
                                            className={modalStyles.primaryBtn}
                                            onClick={handleSave}
                                            disabled={busy || !rowsValid || exceeded}
                                        >
                                            {t('Save')}
                                        </button>
                                    </div>
                                </div>

                                <div className={modalStyles.scroll}>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, width: '100%' }}>
                                        <div className={styles.lineHeader}>
                                            <div>{t('Code ID')}</div>
                                            <div>{accountCardLabel}</div>
                                            <div>{t('TotalAmountIncludingVAT')}</div>
                                            <div>{t('AmountBeforeVAT')}</div>
                                            <div>{t('VAT')}</div>
                                            <div />
                                        </div>

                                        {rows.map((r) => (
                                            <div key={r.key} className={styles.lineCard}>
                                                <div className={styles.lineRow}>
                                                    <AutocompleteSelectFilter
                                                        options={sortCodeOptions}
                                                        selectedValues={findOption(sortCodeOptions, r.sortCodeId)}
                                                        onChange={(_, nv) => updateRow(r.key, () => ({ sortCodeId: nv ? nv.value : '' }))}
                                                        placeholder={t('Code ID')}
                                                        isMultiple={false}
                                                        direction={dir}
                                                        searchable
                                                        clearable
                                                        portalMenu
                                                    />
                                                    <AutocompleteSelectFilter
                                                        options={options}
                                                        selectedValues={findOption(options, r.accountId)}
                                                        onChange={(_, nv) => {
                                                            if (nv?.value === ADD_ACCOUNT_CARD) {
                                                                requestAddAccountCard(r.key);
                                                                return;
                                                            }
                                                            updateRow(r.key, () => ({ accountId: nv ? nv.value : '' }));
                                                        }}
                                                        emptyAction={canAddAccountCard ? {
                                                            label: t('AddAccountCard'),
                                                            onClick: () => requestAddAccountCard(r.key),
                                                        } : null}
                                                        placeholder={accountCardLabel}
                                                        isMultiple={false}
                                                        direction={dir}
                                                        searchable
                                                        clearable
                                                        portalMenu
                                                    />
                                                    <AmountField
                                                        value={r.amount}
                                                        onChange={(raw) => handleGrossChange(r.key, raw)}
                                                        placeholder="100+100"
                                                        inputStyle={inputStyle}
                                                    />
                                                    <AmountField
                                                        value={r.netAmount}
                                                        onChange={(raw) => handleNetChange(r.key, raw)}
                                                        inputStyle={inputStyle}
                                                    />
                                                    <AutocompleteSelectFilter
                                                        options={vatOptions}
                                                        selectedValues={findOption(vatOptions, r.vat)}
                                                        onChange={(_, nv) => handleVatChange(r.key, nv ? nv.value : 0)}
                                                        placeholder={t('VAT')}
                                                        isMultiple={false}
                                                        direction={dir}
                                                        portalMenu
                                                    />
                                                    <Tooltip title={t('Delete')}>
                                                        <span>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => removeRow(r.key)}
                                                                disabled={busy || rows.length === 1}
                                                            >
                                                                <DeleteIcon sx={{ fontSize: 18, color: '#fa0a1e' }} />
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                </div>

                                                <div className={styles.detailRow}>
                                                    <div>
                                                        <div className={styles.fieldLabel}>{t('MonthReportDate')}</div>
                                                        <CustomMonthDatePicker
                                                            date={r.reportingMonthDate}
                                                            onDateChange={(d) => d && updateRow(r.key, () => ({ reportingMonthDate: d.toISOString() }))}
                                                            height="36px"
                                                        />
                                                    </div>
                                                    <div>
                                                        <div className={styles.fieldLabel}>{t('MonthRegisterDate')}</div>
                                                        <CustomMonthDatePicker
                                                            date={r.registrationMonthDate}
                                                            onDateChange={(d) => d && updateRow(r.key, () => ({ registrationMonthDate: d.toISOString() }))}
                                                            height="36px"
                                                        />
                                                    </div>
                                                    <div>
                                                        <div className={styles.fieldLabel}>{t('Currency')}</div>
                                                        <AutocompleteSelectFilter
                                                            options={currencyOptions}
                                                            selectedValues={findOption(currencyOptions, r.foreignCurrencyCode)}
                                                            onChange={(_, nv) => handleCurrencyChange(r.key, nv ? nv.value : '')}
                                                            placeholder={t('Shekel')}
                                                            isMultiple={false}
                                                            direction={dir}
                                                            searchable
                                                            clearable
                                                            portalMenu
                                                        />
                                                    </div>
                                                    <div>
                                                        <div className={styles.fieldLabel}>
                                                            {t('ForeignCurrencyAmount')}
                                                            {rateOf(r) ? ` (${rateOf(r)})` : ''}
                                                        </div>
                                                        <AmountField
                                                            value={r.foreignCurrencyAmount}
                                                            disabled={!hasValue(r.foreignCurrencyCode)}
                                                            onChange={(raw) => handleForeignAmountChange(r.key, raw)}
                                                            inputStyle={inputStyle}
                                                        />
                                                    </div>
                                                </div>

                                                {/* Free text is the longest thing on the line, so it gets its
                                                    own full width under the fields. */}
                                                <div className={styles.descriptionRow}>
                                                    <div>
                                                        <div className={styles.fieldLabel}>{t('Description')}</div>
                                                        <TextField
                                                            value={r.description}
                                                            placeholder={source.description || ''}
                                                            onChange={(e) => {
                                                                const description = e.target.value;
                                                                updateRow(r.key, () => ({ description }));
                                                            }}
                                                            variant="standard"
                                                            InputProps={{ disableUnderline: true }}
                                                            className={fieldStyles.styleInput}
                                                            size="small"
                                                            inputProps={{ ...inputStyle, style: { ...inputStyle.style, fontSize: '0.9rem' } }}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        ))}

                                        <div>
                                            <button
                                                type="button"
                                                className={modalStyles.ghostBtn}
                                                onClick={() => setRows((prev) => [...prev, newRow(prev[prev.length - 1])])}
                                                disabled={busy}
                                            >
                                                <AddIcon sx={{ fontSize: 16, verticalAlign: 'middle' }} /> {t('AddRow')}
                                            </button>
                                        </div>

                                        {/* The document being split, under the lines it is carved into: what is
                                            left of it is the last thing read, next to the remaining amount. */}
                                        <div className={styles.mainBlock}>
                                            <div className={styles.mainTitle}>{t('SplitMainTransaction')}</div>
                                            <div className={styles.summary}>
                                                <div>
                                                    <div className={styles.summaryLabel}>
                                                        {isExpense ? t('Supplier') : t('Client')}
                                                    </div>
                                                    <div className={styles.summaryValue}>
                                                        {source.accountCodeName || accountLabel(partyAccount)}
                                                    </div>
                                                </div>
                                                <div>
                                                    <div className={styles.summaryLabel}>
                                                        {isExpense ? t('Debit Account') : t('Credit Account')}
                                                    </div>
                                                    <div className={styles.summaryValue}>{accountLabel(sourceAccount)}</div>
                                                </div>
                                                <div>
                                                    <div className={styles.summaryLabel}>{t('Code ID')}</div>
                                                    <div className={styles.summaryValue}>{sortCodeLabel(source.sortCodeId)}</div>
                                                </div>
                                                <div>
                                                    <div className={styles.summaryLabel}>{t('Amount')}</div>
                                                    <div className={styles.summaryValue}>{beautifyNumber(amount)}</div>
                                                </div>
                                                <div>
                                                    <div className={styles.summaryLabel}>{t('AmountBeforeVAT')}</div>
                                                    <div className={styles.summaryValue}>{beautifyNumber(amountBeforeVat)}</div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className={styles.footer}>
                                            <span className={styles.total}>
                                                {t('Total')}: {beautifyNumber(total)}
                                            </span>
                                            <span className={exceeded ? styles.exceeded : styles.remaining}>
                                                {exceeded ? t('SplitExceedsAmount') : `${t('SplitMainKeeps')}: ${beautifyNumber(remaining)}`}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            </div>
                            {previewOpen && (
                                <div className={styles.documentPane}>
                                    <div className={styles.documentPaneTitle}>{t('Document')}</div>
                                    <div className={styles.documentFrame}>
                                        <Iframe
                                            key={documentUrl}
                                            url={`${documentUrl}#page=1&view=FitH`}
                                            width="100%"
                                            height="100%"
                                            id={`split-document-${source.id || 'preview'}`}
                                            position="absolute"
                                            styles={{ inset: 0, border: 0 }}
                                            title={source.title || t('Document')}
                                            allowFullScreen
                                        />
                                    </div>
                                </div>
                            )}
                            </div>
                        </div>
                    </div>
                </Draggable>
            </div>
        </Portal>
    );
}
